import axios from 'axios';
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { useToast } from '~/composables/useToast';
import { useI18n } from 'vue-i18n';
import { useRequestHeaders } from '#imports';
import { localeMappings } from '~/i18n/config';

// 创建一个工厂函数来创建 API 客户端
export const createApiClient = (baseURL: string): AxiosInstance => {
  const apiClient: AxiosInstance = axios.create({
    baseURL,
    timeout: 20000, // 普通 API 请求默认超时时间为 10 秒
    headers: {
      'Content-Type': 'text/json',
      'X-CLIENT-VERSION':'1.0.0' ,  
      'X-CLIENT-VERSION-CODE':'182',
      'X-PLATFORM':'web',  
      'X-PLATFORM-VERSION':'1.0.0',  
      'X-CHANNEL':'web',
    }
  });

  // 请求拦截器
  apiClient.interceptors.request.use(
    (config) => {
      // 记录请求信息
      // console.log(`API请求: ${config.method?.toUpperCase()} ${config.url}`, config.data || config.params);
      
      // 只在客户端环境处理本地存储和认证
      if (process.client) {
        // 获取 localStorage 中的语言设置（简短格式）
        const shortLocale = localStorage.getItem('locale') || 'zh';
        
        // 使用导入的语言映射，需要类型断言确保类型安全
        const fullLocale = (shortLocale in localeMappings.shortToFull) 
          ? localeMappings.shortToFull[shortLocale as keyof typeof localeMappings.shortToFull] 
          : 'zh_CN';
        
        // 设置语言头（使用完整格式）
        config.headers['x-language'] = fullLocale;
        
        // 设置时区参数
        // const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Shanghai';
        // config.headers['x-tz'] = timeZone;
        
        // 从localStorage获取token
        const token = localStorage.getItem('auth_token');
        if (token) {
          // 使用Authorization头部传递token
          config.headers['authorization'] = `Bearer ${token}`;
          // 保留原有的X-FCM-TOKEN以兼容旧代码
          // config.headers['x-fcm-token'] = token;
          // console.log('已添加认证token到请求头');
        }
      } else {
        // Public SSR callers already know the selected Nuxt locale. Preserve
        // it instead of falling back to English after the async boundary.
        if (config.headers['x-language']) return config;
        // 服务端环境下获取当前语言设置
        try {
          // 从 Nuxt的useRequestHeaders 获取 cookie
          const headers = useRequestHeaders(['cookie']);
          const cookies = headers.cookie || '';
          
          // 解析cookie获取i18n_redirected (Nuxt i18n cookie)
          const i18nCookie = cookies.split(';').find((c: string) => c.trim().startsWith('i18n_redirected='));
          let locale = 'en'; // 默认语言
          
          if (i18nCookie) {
            locale = i18nCookie.split('=')[1].trim();
          }
          
          // 使用导入的语言映射，需要类型断言确保类型安全
          const fullLocale = (locale in localeMappings.shortToFull) 
            ? localeMappings.shortToFull[locale as keyof typeof localeMappings.shortToFull] 
            : 'en_US';
          config.headers['x-language'] = fullLocale;
        } catch (error) {
          // 出错时使用默认语言
          config.headers['x-language'] = 'en_US';
        }
      }
      
      return config;
    },
    (error) => {
      console.error('请求拦截器错误:', error);
      return Promise.reject(error);
    }
  );

  // 响应拦截器
  apiClient.interceptors.response.use(
    (response) => {

      // console.log(`API响应: ${response.config.method?.toUpperCase()} ${response.config.url}`, response.data);
      return response;
    },
    (error) => {
      const { response } = error;
      
      if (response) {
        // 处理不同的错误状态码
        switch (response.status) {
          case 401: // 未授权
            // 清除token并重定向到登录页
            if (process.client) {
              // console.log('401错误: 清除token并重定向');
              
              // 使用toast服务
              const toast = useToast();
              
              // 获取i18n实例
              const { t } = useI18n();
              toast.error(t('user.sessionExpired'));
              
              localStorage.removeItem('auth_token');
              console.warn('401 from backend. Cleared only local API token and kept Clerk session intact.');
            }
            break;
          case 403: // 禁止访问
            console.error('403错误: 没有权限访问此资源');
            break;
          case 404: // 资源不存在
            console.error('404错误: 请求的资源不存在');
            break;
          case 500: // 服务器错误
            console.error('500错误: 服务器错误');
            break;
          default:
            console.error(`未处理的错误状态码: ${response.status}`);
        }
        
        // 尝试获取错误消息
        let errorMessage = '';
        if (response.data && response.data.message) {
          errorMessage = response.data.message;
        } else if (response.data && response.data.error) {
          errorMessage = response.data.error;
        } else if (response.data && response.data.msg) {
          errorMessage = response.data.msg;
        }
        
        // 显示错误消息（如果在客户端）
        if (process.client) {
          // 使用全局事件总线触发错误事件，让组件可以监听并显示 Toast
          window.dispatchEvent(new CustomEvent('api-error', { 
            detail: { 
              message: errorMessage,
              status: response.status,
              url: response.config?.url
            } 
          }));
        }
      } else {
        // 网络错误或请求被取消
        console.error('网络错误或请求被取消:', error.message);
        
        // 显示网络错误消息（如果在客户端）
        if (process.client) {
          window.dispatchEvent(new CustomEvent('api-error', { 
            detail: { 
              message: error.message || '',
              status: 0,
              url: error.config?.url
            } 
          }));
        }
      }
      
      return Promise.reject(error);
    }
  );

  return apiClient;
};

// 创建一个默认的 API 对象，将在插件中初始化
let _apiClient: AxiosInstance | null = null;

// 初始化 API 客户端的函数
export const initApiClient = (baseURL: string) => {
  _apiClient = createApiClient(baseURL);
};

// 获取 API 客户端的函数
export const getApiClient = (): AxiosInstance => {
  if (!_apiClient) {
    throw new Error('API 客户端尚未初始化');
  }
  return _apiClient;
};

// API 请求方法
export const api = {
  // GET请求
  get<T = any>(url: string, params?: any, config?: AxiosRequestConfig): Promise<T> {
    return getApiClient().get(url, { params, ...config })
      .then((response: AxiosResponse<T>) => response.data);
  },
  
  // POST请求
  post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return getApiClient().post(url, data, config)
      .then((response: AxiosResponse<T>) => response.data);
  },
  
  // PUT请求
  put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return getApiClient().put(url, data, config)
      .then((response: AxiosResponse<T>) => response.data);
  },
  
  // DELETE请求
  delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return getApiClient().delete(url, config)
      .then((response: AxiosResponse<T>) => response.data);
  },
  
  // PATCH请求
  patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return getApiClient().patch(url, data, config)
      .then((response: AxiosResponse<T>) => response.data);
  },
  
  // 文件上传
  uploadFile<T = any>(url: string, file: File, onProgress?: (progress: number) => void, config?: AxiosRequestConfig): Promise<T> {
    const formData = new FormData();
    formData.append('image', file);

    return getApiClient().post(url, formData, {
      ...config,
      timeout: 60000, // 文件上传超时时间设置为 60 秒
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = (progressEvent.loaded / progressEvent.total) * 100;
          onProgress(progress);
        }
      },
    }).then((response: AxiosResponse<T>) => response.data);
  },
};

// 导出默认实例和方法
export default api;
